// client/src/components/CashierAnalyseTransaction.js

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { FaArrowLeft, FaGift, FaShoppingCart, FaBullseye } from 'react-icons/fa';
import CashierNavbar from './CashierNavbar';
import { firestore } from '../../firebase';

const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

const parseFlexibleNumber = (val) => {
  if (val === null || val === undefined) return 0;
  const s = String(val).replace(',', '.').trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const formatPts = (n) => {
  const value = parseFlexibleNumber(n);
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const toDate = (value) => {
  if (!value) return new Date(0);
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  return new Date(value);
};

const CashierAnalyseTransaction = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [cashierUid, setCashierUid] = useState(null);
  const [companyUid, setCompanyUid] = useState(null);
  const [settings, setSettings] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  // eslint-disable-next-line
  const [customerProfile, setCustomerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        setCashierUid(null);
        setCompanyUid(null);
        setCustomerData(null);
        setLoading(false);
        return;
      }
      setCashierUid(currentUser.uid);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!cashierUid) return;
    const userDocRef = doc(firestore, 'users', cashierUid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (snap) => {
        if (!snap.exists()) {
          setCompanyUid(null);
          return;
        }
        const data = snap.data() || {};
        setCompanyUid(data.companyuid || null);
      },
      (error) => {
        console.error('Cashier user listener error', error);
        setCompanyUid(null);
      }
    );
    return () => unsubscribe();
  }, [cashierUid]);

  useEffect(() => {
    if (!companyUid) {
      setSettings(null);
      return;
    }
    const fidelityRef = doc(firestore, 'fidelity', companyUid);
    const unsubscribe = onSnapshot(
      fidelityRef,
      (snap) => {
        if (!snap.exists()) {
          setSettings(null);
          return;
        }
        const data = snap.data() || {};
        const type = data.Type || 'Fidbycount';
        const multiple = parseInt(data.multiple ?? 5, 10) || 5;
        const pointsPaliers = [
          { points: data.points1, gift: data.gift1 },
          { points: data.points2, gift: data.gift2 },
          { points: data.points3, gift: data.gift3 },
        ].filter((p) => parseFlexibleNumber(p.points) > 0);
        setSettings({
          type,
          multiple,
          gift: data.gift || '',
          pointsPaliers: type === 'Fidbypoints' ? pointsPaliers : [],
        });
      },
      (error) => { console.error('Settings listener error', error); }
    );
    return () => unsubscribe();
  }, [companyUid]);

  useEffect(() => {
    if (!companyUid || !customerId) {
      setCustomerData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const listDocRef = doc(firestore, 'fidelity', companyUid, 'list', customerId);
    const unsubscribe = onSnapshot(
      listDocRef,
      (snap) => {
        setCustomerData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (error) => {
        console.error('Customer listener error', error);
        setCustomerData(null);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [companyUid, customerId]);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) return undefined;
    (async () => {
      try {
        const userSnap = await getDoc(doc(firestore, 'users', customerId));
        if (cancelled) return;
        setCustomerProfile(userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null);
      } catch (error) {
        console.error('Customer profile fetch error', error);
        if (!cancelled) setCustomerProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const flattenedHistory = useMemo(() => {
    if (!customerData) return [];
    const timestamps = (customerData.timestamps || []).map((ts, index) => ({
      id: `ts-${index}`,
      date: toDate(ts.date),
      type: 'timestamp',
      count: ts.count ?? 1,
    }));

    const resetstamps = (customerData.resetstamp || []).map((rs, index) => ({
      id: `rs-${index}`,
      date: toDate(rs.date),
      type: 'resetstamp',
      count: rs.count ?? 1,
      giftAtTime: rs.giftAtTime,
    }));

    const pointshistory = (customerData.pointshistory || []).map((ph, index) => ({
      id: `ph-${index}`,
      date: toDate(ph.date),
      type: 'pointshistory',
      points: parseFlexibleNumber(ph.points),
      montant: parseFlexibleNumber(ph.montant),
      gift: ph.gift,
    }));

    return [...timestamps, ...resetstamps, ...pointshistory].sort((a, b) => b.date - a.date);
  }, [customerData]);

  const compteur = parseInt(customerData?.compteur, 10) || 0;
  const points = parseFlexibleNumber(customerData?.points);
  const multiple = settings?.multiple || 0;

  const visibleHistory = showAll ? flattenedHistory : flattenedHistory.slice(0, 12);

  return (
    <div>
      <CashierNavbar />
      <div className="dashboard-container">
        <style>{`
          .ca-page { width: 100%; min-width: 0; display: flex; flex-direction: column; gap: 24px; }
          .ca-page .eb-page-header { margin-bottom: 0; }
          .ca-page .eb-page-title { margin: 0; text-align: left; }
          .ca-state { padding: 40px 24px; display: flex; flex-direction: column; align-items: center; gap: 14px; text-align: center; color: var(--eb-muted); font-size: 14.5px; }
          .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 16px; }
          .summary-block { background: var(--eb-surface); border-radius: var(--eb-radius-lg); padding: 20px; border: 1px solid var(--eb-border); box-shadow: var(--eb-shadow-sm); display: flex; flex-direction: column; gap: 6px; }
          .summary-block--reached { background: var(--eb-warning-bg); border-color: #fde3b0; }
          .summary-label { font-size: 12.5px; font-weight: 600; color: var(--eb-muted); text-transform: uppercase; letter-spacing: 0.05em; }
          .summary-value { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); line-height: 1.15; }
          .summary-strong { font-weight: 650; color: var(--eb-text); display: inline-flex; align-items: center; gap: 8px; }
          .summary-muted { color: var(--eb-muted); font-size: 14px; }
          .ca-section-title { margin: 0 0 12px; font-size: 17px; font-weight: 650; color: var(--eb-text); text-align: left; }
          .table-fidelity-wrap { width: 100%; overflow-x: auto; }
          .table-fidelity { width: 100%; border-collapse: separate; border-spacing: 0; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); overflow: hidden; box-shadow: var(--eb-shadow-sm); font-size: 14px; }
          .table-fidelity thead th { background: var(--eb-surface-2); color: var(--eb-muted); font-weight: 600; font-size: 12.5px; text-transform: uppercase; letter-spacing: 0.04em; white-space: nowrap; border-bottom: 1px solid var(--eb-border); padding: 12px 16px; text-align: left; }
          .table-fidelity td { padding: 12px 16px; text-align: left; vertical-align: middle; word-break: break-word; color: var(--eb-text-2); border-bottom: 1px solid var(--eb-border); }
          .table-fidelity tbody tr:last-child td { border-bottom: 0; }
          .table-fidelity tbody tr:hover { background: var(--eb-brand-50); }
          .table-fidelity .td-date { color: var(--eb-muted); white-space: nowrap; }
          .pill { display: inline-flex; align-items: center; gap: 6px; font-weight: 700; padding: 4px 10px; border-radius: var(--eb-radius-pill); border: 1px solid transparent; font-size: 13.5px; white-space: nowrap; }
          .pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
          .pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
          .pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
          .pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }
          .ca-more { display: flex; justify-content: center; margin-top: 16px; }
          @media (max-width: 768px) {
            .table-fidelity thead th, .table-fidelity td { padding: 10px 10px; font-size: 13.5px; }
            .table-fidelity thead th { font-size: 11.5px; }
            .summary-value { font-size: 24px; }
          }
        `}</style>

        <div className="ca-page">
          <header className="eb-page-header">
            <div>
              <h1 className="eb-page-title">Fiche fidélité client</h1>
              <p className="eb-page-subtitle">Solde et historique des transactions de ce client.</p>
            </div>
            <button onClick={() => navigate('/cashierdashboard')} className="eb-btn eb-btn--secondary">
              <FaArrowLeft aria-hidden="true" /> Retour
            </button>
          </header>

          {loading ? (
            <div className="eb-card ca-state">
              <span className="eb-spinner" aria-hidden="true" />
              Chargement des transactions…
            </div>
          ) : !customerData ? (
            <div className="eb-card ca-state">Aucune transaction trouvée pour ce client.</div>
          ) : (
            <>
              <div className="summary-grid">
                {settings?.type === 'Fidbypoints' ? (
                  <>
                    <div className="summary-block">
                      <span className="summary-label">Points cumulés</span>
                      <span className="summary-value">{formatPts(points)} pts</span>
                    </div>
                    {settings.pointsPaliers.map((palier, index) => {
                      const reached = points >= parseFlexibleNumber(palier.points);
                      return (
                        <div key={index} className={`summary-block${reached ? ' summary-block--reached' : ''}`}>
                          <span className={`eb-badge${reached ? ' eb-badge--warning' : ''}`} style={{ alignSelf: 'flex-start' }}>
                            {reached ? <FaGift aria-hidden="true" /> : <FaBullseye aria-hidden="true" />}
                            {reached ? 'Utilisable' : 'Objectif'}
                          </span>
                          <span className="summary-strong">{palier.gift}</span>
                          <span className="summary-muted">{formatPts(palier.points)} pts requis</span>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div className="summary-block">
                      <span className="summary-label">Passages validés</span>
                      <span className="summary-value">
                        {compteur}{multiple ? ` / ${multiple}` : ''} passages
                      </span>
                    </div>
                    {settings?.gift && (
                      <div className="summary-block">
                        <span className="summary-label">Prochain gain</span>
                        <span className="summary-strong"><FaGift aria-hidden="true" style={{ color: 'var(--eb-warning)' }} />{settings.gift}</span>
                        {multiple ? <span className="summary-muted">Après {multiple} validations</span> : null}
                      </div>
                    )}
                  </>
                )}
              </div>

              <section>
                <h2 className="ca-section-title">Historique des transactions</h2>
                {flattenedHistory.length === 0 ? (
                  <div className="eb-card ca-state">Aucun historique disponible.</div>
                ) : (
                  <>
                    <div className="table-fidelity-wrap">
                      <table className="table-fidelity">
                        <thead>
                          <tr>
                            <th>Valeur</th>
                            <th>Date</th>
                            <th>Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleHistory.map((row) => {
                            const formattedDate = formatCompactDateFR(row.date);
                            let value = '';
                            let typeLabel = '';
                            let pillClass = 'pill';

                            if (row.type === 'timestamp') {
                              typeLabel = 'Scans';
                              const count = row.count || 1;
                              value = (
                                <>
                                  <FaShoppingCart aria-hidden="true" />
                                  +{count}
                                </>
                              );
                              pillClass += ' pill-scan';
                            } else if (row.type === 'resetstamp') {
                              typeLabel = 'Gain utilisé';
                              value = (
                                <>
                                  <FaGift aria-hidden="true" />
                                  {row.count || 1}
                                </>
                              );
                              pillClass += ' pill-gift';
                            } else {
                              const pts = row.points || 0;
                              const isPositive = pts > 0;
                              typeLabel = isPositive ? 'Points ajoutés' : 'Points dépensés';
                              value = (
                                <>
                                  {isPositive ? <FaShoppingCart aria-hidden="true" /> : <FaGift aria-hidden="true" />}
                                  {isPositive ? '+' : ''}{formatPts(pts)} pts
                                </>
                              );
                              pillClass += isPositive ? ' pill-pos' : ' pill-neg';
                            }

                            return (
                              <tr key={row.id}>
                                <td>
                                  <span className={pillClass}>{value}</span>
                                </td>
                                <td className="td-date">{formattedDate}</td>
                                <td>{typeLabel}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {flattenedHistory.length > visibleHistory.length && (
                      <div className="ca-more">
                        <button type="button" className="eb-btn eb-btn--secondary" onClick={() => setShowAll((prev) => !prev)}>
                          {showAll ? 'Afficher moins' : 'Afficher plus'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashierAnalyseTransaction;
